import { useState } from 'react';
import { useUEBAEntities, useUEBAAnomaly, type UEBAEntity, type UEBAAnomaly } from '../../hooks/useSecOps';
import { secopsApi } from '../../lib/secops-api';

function RiskBadge({ score }: { score: number }) {
  const color = score >= 75 ? 'bg-red-500/10 text-red-400 border-red-500/20' :
    score >= 50 ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
    score >= 25 ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
    'bg-green-500/10 text-green-400 border-green-500/20';
  const label = score >= 75 ? 'Critical' : score >= 50 ? 'High' : score >= 25 ? 'Medium' : 'Low';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {score} - {label}
    </span>
  );
}

function RiskGauge({ score }: { score: number }) {
  const color = score >= 75 ? '#dc2626' : score >= 50 ? '#d97706' : score >= 25 ? '#eab308' : '#16a34a';
  const label = score >= 75 ? 'Critical' : score >= 50 ? 'High' : score >= 25 ? 'Medium' : 'Low';
  const rotation = (score / 100) * 180 - 90;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-28 h-14 overflow-hidden">
        <svg viewBox="0 0 120 60" className="w-full h-full">
          <path d="M10 55 A50 50 0 0 1 110 55" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" strokeLinecap="round" />
          <path d="M10 55 A50 50 0 0 1 110 55" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${(score / 100) * 157} 157`} />
          <line x1="60" y1="55" x2="60" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round"
            transform={`rotate(${rotation} 60 55)`} />
        </svg>
      </div>
      <span className="text-xl font-bold mt-1" style={{ color }}>{score}</span>
      <span className="text-[10px] text-white/40">{label} Risk</span>
    </div>
  );
}

function EntityTypeIcon({ type }: { type: 'user' | 'device' }) {
  if (type === 'user') {
    return (
      <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
    </svg>
  );
}

function AnomalyTypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    unusual_hours: 'Unusual Hours',
    volume_spike: 'Volume Spike',
    new_system_access: 'New System',
    lateral_movement: 'Lateral Movement',
    privilege_escalation: 'Privilege Escalation',
  };
  const colors: Record<string, string> = {
    unusual_hours: 'bg-yellow-500/10 text-yellow-400',
    volume_spike: 'bg-orange-500/10 text-orange-400',
    new_system_access: 'bg-blue-500/10 text-blue-400',
    lateral_movement: 'bg-purple-500/10 text-purple-400',
    privilege_escalation: 'bg-red-500/10 text-red-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${colors[type] ?? 'bg-white/5 text-white/60'}`}>
      {labels[type] ?? type}
    </span>
  );
}

function SeverityDot({ severity }: { severity: string }) {
  const color = severity === 'critical' ? 'bg-red-500' : severity === 'high' ? 'bg-orange-500' : severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500';
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

interface EntityDetailPanelProps {
  entityId: string;
  onClose: () => void;
}

function EntityDetailPanel({ entityId, onClose }: EntityDetailPanelProps) {
  const [detail, setDetail] = useState<{
    entity_name: string;
    entity_type: 'user' | 'device';
    risk_score: number;
    anomaly_count: number;
    anomalies: UEBAAnomaly[];
    baseline: { normal_login_hours: { start: number; end: number }; avg_daily_access_count: number; systems_accessed: string[]; sample_days: number } | null;
    timeline: Array<{ id: string; event: string; severity: string; timestamp: string; category?: string }>;
    last_seen: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useState(() => {
    secopsApi.get<{ data: typeof detail }>(`/secops/ueba/entities/${entityId}`)
      .then(res => setDetail(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#111] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-[#111] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Entity Detail</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>

        {loading && (
          <div className="p-6 space-y-4">
            {[1,2,3].map(i => <div key={i} className="bg-white/5 h-20 rounded-lg animate-pulse" />)}
          </div>
        )}

        {detail && (
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-white/5">
                <EntityTypeIcon type={detail.entity_type} />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">{detail.entity_name}</h3>
                <p className="text-white/40 text-xs capitalize">{detail.entity_type}</p>
                {detail.last_seen && (
                  <p className="text-white/30 text-[10px] mt-1">Last seen: {new Date(detail.last_seen).toLocaleString()}</p>
                )}
              </div>
              <RiskGauge score={detail.risk_score} />
            </div>

            {/* Baseline Stats */}
            {detail.baseline && (
              <div>
                <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Baseline</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                    <p className="text-[10px] text-white/40">Normal Hours</p>
                    <p className="text-sm text-white font-medium">{detail.baseline.normal_login_hours.start}:00 - {detail.baseline.normal_login_hours.end}:00 UTC</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                    <p className="text-[10px] text-white/40">Avg Daily Activity</p>
                    <p className="text-sm text-white font-medium">{detail.baseline.avg_daily_access_count} events</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5 col-span-2">
                    <p className="text-[10px] text-white/40">Systems Accessed ({detail.baseline.systems_accessed.length})</p>
                    <p className="text-xs text-white/70 mt-1 truncate">{detail.baseline.systems_accessed.join(', ') || 'None tracked'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Anomalies */}
            <div>
              <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Anomalies ({detail.anomaly_count})</h4>
              {detail.anomalies.length === 0 && (
                <p className="text-white/30 text-sm">No anomalies detected</p>
              )}
              <div className="space-y-2">
                {detail.anomalies.map(a => (
                  <div key={a.id} className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <SeverityDot severity={a.severity} />
                      <AnomalyTypeBadge type={a.anomaly_type} />
                      <span className="text-[10px] text-white/30 ml-auto">+{a.risk_contribution} risk</span>
                    </div>
                    <p className="text-xs text-white/70">{a.description}</p>
                    <p className="text-[10px] text-white/30 mt-1">{new Date(a.detected_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Activity Timeline</h4>
              {detail.timeline.length === 0 && (
                <p className="text-white/30 text-sm">No recent activity</p>
              )}
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {detail.timeline.slice(0, 50).map(t => (
                  <div key={t.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.03]">
                    <SeverityDot severity={t.severity} />
                    <span className="text-xs text-white/70 flex-1 truncate">{t.event}</span>
                    <span className="text-[10px] text-white/30 whitespace-nowrap">{new Date(t.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UEBAPage() {
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [riskFilter, setRiskFilter] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);

  const filters: Record<string, string> = {};
  if (typeFilter) filters.type = typeFilter;
  if (riskFilter) filters.risk_level = riskFilter;

  const { data: entitiesResponse, loading: entitiesLoading, refresh: refreshEntities } = useUEBAEntities(filters);
  const { data: anomaliesResponse, loading: anomaliesLoading } = useUEBAAnomaly();

  const entities = entitiesResponse?.data?.entities ?? [];
  const anomalies = anomaliesResponse?.data?.anomalies ?? [];

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await secopsApi.post('/secops/ueba/baseline', {});
      refreshEntities();
    } catch { /* ignore */ }
    finally { setRecalculating(false); }
  };

  // Summary stats
  const criticalCount = entities.filter(e => e.risk_score >= 75).length;
  const highCount = entities.filter(e => e.risk_score >= 50 && e.risk_score < 75).length;
  const totalAnomalies = entities.reduce((sum, e) => sum + e.anomaly_count, 0);

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">User & Entity Behavior Analytics</h1>
          <p className="text-xs text-white/40 mt-1">Monitor user and device behavior patterns, detect anomalies from baseline activity</p>
        </div>
        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white transition-colors"
        >
          {recalculating ? 'Computing...' : 'Recalculate Baselines'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[10px] text-white/40 uppercase tracking-wider">Total Entities</p>
          <p className="text-2xl font-bold text-white mt-1">{entities.length}</p>
        </div>
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
          <p className="text-[10px] text-red-400 uppercase tracking-wider">Critical Risk</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{criticalCount}</p>
        </div>
        <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-4">
          <p className="text-[10px] text-orange-400 uppercase tracking-wider">High Risk</p>
          <p className="text-2xl font-bold text-orange-400 mt-1">{highCount}</p>
        </div>
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4">
          <p className="text-[10px] text-yellow-400 uppercase tracking-wider">Active Anomalies</p>
          <p className="text-2xl font-bold text-yellow-400 mt-1">{totalAnomalies}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 focus:outline-none focus:border-indigo-500"
        >
          <option value="">All Types</option>
          <option value="user">Users</option>
          <option value="device">Devices</option>
        </select>
        <select
          value={riskFilter}
          onChange={e => setRiskFilter(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 focus:outline-none focus:border-indigo-500"
        >
          <option value="">All Risk Levels</option>
          <option value="critical">Critical (75+)</option>
          <option value="high">High (50-74)</option>
          <option value="medium">Medium (25-49)</option>
          <option value="low">Low (0-24)</option>
        </select>
      </div>

      {/* Entity Table */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10">
          <h2 className="text-sm font-semibold text-white">Monitored Entities</h2>
        </div>

        {entitiesLoading && entities.length === 0 ? (
          <div className="p-8 text-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent mx-auto" />
            <p className="text-white/30 text-sm mt-2">Loading entities...</p>
          </div>
        ) : entities.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-white/30 text-sm">No entities found. Run baseline calculation to start monitoring.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-[10px] text-white/40 uppercase tracking-wider px-4 py-2 font-medium">Entity</th>
                <th className="text-left text-[10px] text-white/40 uppercase tracking-wider px-4 py-2 font-medium">Type</th>
                <th className="text-left text-[10px] text-white/40 uppercase tracking-wider px-4 py-2 font-medium">Risk Score</th>
                <th className="text-left text-[10px] text-white/40 uppercase tracking-wider px-4 py-2 font-medium">Anomalies</th>
                <th className="text-left text-[10px] text-white/40 uppercase tracking-wider px-4 py-2 font-medium">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {entities.map((entity: UEBAEntity) => (
                <tr
                  key={entity.id}
                  className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  onClick={() => setSelectedEntity(entity.entity_id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <EntityTypeIcon type={entity.entity_type} />
                      <span className="text-sm text-white font-medium">{entity.entity_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-white/50 capitalize">{entity.entity_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <RiskBadge score={entity.risk_score} />
                  </td>
                  <td className="px-4 py-3">
                    {entity.anomaly_count > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-xs">
                        {entity.anomaly_count}
                      </span>
                    ) : (
                      <span className="text-xs text-white/30">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-white/40">
                      {entity.last_seen ? new Date(entity.last_seen).toLocaleDateString() : '--'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent Anomalies */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10">
          <h2 className="text-sm font-semibold text-white">Recent Anomalies</h2>
        </div>

        {anomaliesLoading && anomalies.length === 0 ? (
          <div className="p-6 text-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent mx-auto" />
          </div>
        ) : anomalies.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-white/30 text-sm">No anomalies detected. Baselines may need to be computed first.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {anomalies.slice(0, 20).map((anomaly: UEBAAnomaly & { entity_name?: string; entity_type?: string }) => (
              <div key={anomaly.id} className="px-4 py-3 hover:bg-white/[0.02] flex items-center gap-3">
                <SeverityDot severity={anomaly.severity} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <AnomalyTypeBadge type={anomaly.anomaly_type} />
                    {anomaly.entity_name && (
                      <span className="text-xs text-white/50">{anomaly.entity_name}</span>
                    )}
                  </div>
                  <p className="text-xs text-white/60 truncate">{anomaly.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs text-white/30 block">+{anomaly.risk_contribution}</span>
                  <span className="text-[10px] text-white/20">{new Date(anomaly.detected_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slide-out detail panel */}
      {selectedEntity && (
        <EntityDetailPanel entityId={selectedEntity} onClose={() => setSelectedEntity(null)} />
      )}
    </div>
  );
}
